import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, limit } from 'firebase/firestore';
import { db } from '../../firebase';
import Layout from '../../components/ui/Layout';
import { Container, Spinner } from 'react-bootstrap';
import SupermarketProducts from './SupermarketProducts';
import ProductDetailsPage from './ProductDetails';
import { slugify } from '../../constants/site';
import { createLogger } from '../../utils/logger';

const logger = createLogger('SupermarketOrProductResolver');

const FIREBASE_ID_PATTERN = /^[a-zA-Z0-9]{20}$/;

export default function SupermarketOrProductResolver() {
  const { supermarketId, slug } = useParams();
  const [resolved, setResolved] = useState(null); // 'product' | 'supermarket'
  const [resolvedProductId, setResolvedProductId] = useState(null); // when resolved as product (from seo_name lookup)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const resolve = async () => {
      if (!supermarketId || !slug) {
        setResolved('supermarket');
        setLoading(false);
        return;
      }

      try {
        // Heuristic: try product by ID first if slug looks like Firebase ID
        if (FIREBASE_ID_PATTERN.test(slug)) {
          const productSnap = await getDoc(doc(db, 'Products', slug));
          const invQuery = query(
            collection(db, 'Inventory'),
            where('product_id', '==', slug),
            where('supermarket_id', '==', supermarketId)
          );
          const invSnap = await getDocs(invQuery);
          const invWithStock = invSnap.docs.filter(d => (d.data().quantity ?? 0) > 0);
          if (productSnap.exists() && invWithStock.length > 0) {
            setResolvedProductId(slug);
            setResolved('product');
            setLoading(false);
            return;
          }
        }

        // Try product by seo_name
        const productSeoQuery = query(
          collection(db, 'Products'),
          where('seo_name', '==', slug)
        );
        const productSeoSnap = await getDocs(productSeoQuery);
        if (!productSeoSnap.empty) {
          const product = productSeoSnap.docs[0];
          const invQuery = query(
            collection(db, 'Inventory'),
            where('product_id', '==', product.id),
            where('supermarket_id', '==', supermarketId)
          );
          const invSnap = await getDocs(invQuery);
          const seoInvWithStock = invSnap.docs.filter(d => (d.data().quantity ?? 0) > 0);
          if (seoInvWithStock.length > 0) {
            setResolvedProductId(product.id);
            setResolved('product');
            setLoading(false);
            return;
          }
        }

        if (!FIREBASE_ID_PATTERN.test(slug)) {
          const productsSnap = await getDocs(query(collection(db, 'Products'), limit(300)));
          const match = productsSnap.docs.find(d => slugify(d.data()?.display_name) === slug);
          if (match) {
            const invQuery = query(
              collection(db, 'Inventory'),
              where('product_id', '==', match.id),
              where('supermarket_id', '==', supermarketId)
            );
            const invSnap = await getDocs(invQuery);
            const invWithStock = invSnap.docs.filter(d => (d.data().quantity ?? 0) > 0);
            if (invWithStock.length > 0) {
              setResolvedProductId(match.id);
              setResolved('product');
              setLoading(false);
              return;
            }
          }
        }

        // Not a product - treat as supermarket seo_name or unknown slug
        setResolved('supermarket');
      } catch (err) {
        logger.error('Resolver error:', err);
        setResolved('supermarket');
      }
      setLoading(false);
    };

    resolve();
  }, [supermarketId, slug]);

  if (loading) {
    return (
      <Layout>
        <Container className="mt-4 text-center">
          <Spinner animation="border" />
          <p>Loading...</p>
        </Container>
      </Layout>
    );
  }

  if (resolved === 'product') {
    return (
      <ProductDetailsPage
        productIdProp={resolvedProductId}
      />
    );
  }

  return <SupermarketProducts />;
}
